import { Controller } from "@hotwired/stimulus"
import { apiJson, formatApiErrorDetail } from "../lib/api_errore"
import { Disegno, h, sincronizza, testo } from "../lib/disegno"

// L'istruttoria assistita del bando nella scheda di verifica (components/verifica/IstruttoriaAI.js di 31a5bd1,
// verifica/_istruttoria_ai). Solo l'amministratore della piattaforma: le API rispondono 403 a chiunque altro.
// - Fornitore (OpenAI o Mistral): cambiarlo è come il remount con key={grantId:provider} dell'originale: selezione,
//   perimetro, consenso, ripresa, sessione archiviata e prova del metodo ripartono da zero e si rilegge con ?provider=.
// - «Avvia istruttoria» solo con chiave configurata, documenti scelti (1–30, entro i caratteri), consenso esplicito;
//   un solo POST per avvio (il pulsante si spegne prima della richiesta). Cambiare documenti, perimetro o ripresa
//   chiede di nuovo il consenso.
// - Mentre la sessione è in_corso (o si aspetta l'esito dell'avvio) si rilegge ogni 4 s; lo stesso per la prova del
//   metodo. Le letture si fermano quando l'elemento esce dalla pagina.
// - Testi dell'AI e nomi dei documenti entrano come testo (lib/disegno). La chiave API non arriva mai qui: solo
//   «configurata» sì/no.
// - La scheda si rilegge con morph dopo ogni azione dei passi: questo elemento no (trattieni), come il componente
//   React restava montato con il suo stato.
const err = (e) => formatApiErrorDetail(e?.detail) || "Istruttoria non disponibile: riprova."
const quando = (s) => (s ? new Date(s).toLocaleString("it-IT", { timeZone: "Europe/Rome" }) : "")
const num = (n) => Number(n).toLocaleString("it-IT")
const token = (uso, k) => num((uso || []).reduce((n, x) => n + (x[k] || 0), 0))

export default class extends Controller {
  static targets = ["fornitore", "sezione", "titolo", "intro", "metodo", "metodoTitolo", "metodoIntro"]
  static values = { grant: String, classi: Object, intervallo: { type: Number, default: 4000 } }

  connect() {
    this.attivo = true
    this.provider = this.fornitoreTarget.value
    this.monta()
  }

  disconnect() {
    this.attivo = false
    this.ferma()
  }

  trattieni(event) {
    if (event.target === this.element) event.preventDefault()
  }

  fornitore(event) {
    this.provider = event.target.value
    this.ferma()
    this.metodoTarget.open = false
    this.monta()
  }

  // ---- stato iniziale (mount) ------------------------------------------------------------------------------------

  monta() {
    this.gen = (this.gen || 0) + 1
    Object.assign(this, { dati: null, selected: [], focus: "", confirmed: false, error: "", busy: false, waiting: null,
                          resume: false, archived: null, historyBusy: false })
    Object.assign(this, { mData: null, mError: "", mBusy: false, mWaiting: null })
    this.disegno = new Disegno()
    this.disegnoMetodo = new Disegno()
    this.sezioneTarget.setAttribute("aria-busy", "true")
    this.metodoTarget.setAttribute("aria-busy", "true")
    this.render()
    this.renderMetodo()
    this.load().catch((e) => this.mostraErrore(e)).finally(() => this.caricata(this.sezioneTarget))
    this.loadMetodo().catch((e) => { if (this.attuale()) { this.mError = err(e); this.renderMetodo() } })
      .finally(() => this.caricata(this.metodoTarget))
  }

  attuale(gen = this.gen) {
    return this.attivo && gen === this.gen
  }

  // aria-busy finché la prima lettura (della sessione o delle prove) non è conclusa
  caricata(el) {
    if (this.attivo) el.removeAttribute("aria-busy")
  }

  ferma() {
    clearInterval(this.timer)
    clearInterval(this.timerMetodo)
    this.timer = null
    this.timerMetodo = null
    this.segnaRilettura()
  }

  // data-rilettura sulla sezione (o sulla prova del metodo) finché la sua lettura periodica è attiva
  segnaRilettura() {
    if (this.timer) this.sezioneTarget.dataset.rilettura = "attiva"
    else delete this.sezioneTarget.dataset.rilettura
    if (this.timerMetodo) this.metodoTarget.dataset.rilettura = "attiva"
    else delete this.metodoTarget.dataset.rilettura
  }

  intervalloValueChanged() {
    if (!this.attivo) return
    const prima = [!!this.timer, !!this.timerMetodo]
    this.ferma()
    if (prima[0]) this.programma()
    if (prima[1]) this.programmaMetodo()
  }

  // ---- istruttoria del fornitore ---------------------------------------------------------------------------------

  get nome() {
    return this.provider === "mistral" ? "Mistral" : "OpenAI"
  }

  get query() {
    return this.provider === "mistral" ? "?provider=mistral" : ""
  }

  get base() {
    return `/api/admin/verifica/${encodeURIComponent(this.grantValue)}/istruttoria-ai`
  }

  async load() {
    const gen = this.gen
    const d = await apiJson(`${this.base}${this.query}`)
    if (!this.attuale(gen)) throw Object.assign(new Error("superata"), { superata: true })
    this.dati = d
    this.render()
    return d
  }

  mostraErrore(e) {
    if (e?.superata || !this.attivo) return
    this.error = err(e)
    this.render()
  }

  ricarica() {
    this.load().catch((e) => this.mostraErrore(e))
  }

  get running() {
    return this.dati?.sessione?.stato === "in_corso"
  }

  // il setInterval dell'originale: attivo finché la sessione è in corso o si aspetta l'esito dell'avvio
  programma() {
    const serve = this.running || !!this.waiting
    if (serve && !this.timer) {
      this.timer = setInterval(() => {
        this.load().then((d) => {
          if (this.waiting && d.sessione.run_id === this.waiting && d.sessione.stato !== "in_corso") {
            this.waiting = null
            this.render()
          }
        }).catch((e) => {
          if (e?.superata || !this.attivo) return
          this.error = err(e)
          this.waiting = null
          this.render()
        })
      }, this.intervalloValue)
    } else if (!serve && this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    this.segnaRilettura()
  }

  change(ids) {
    this.selected = ids
    this.confirmed = false
    this.resume = false
    this.render()
  }

  get chars() {
    return (this.dati?.documenti || []).filter((d) => this.selected.includes(d.doc_id)).reduce((n, d) => n + d.caratteri, 0)
  }

  avvioSpento() {
    const cfg = this.dati?.configurazione
    return !cfg || this.busy || this.running || !!this.waiting || !cfg.configurata || !this.confirmed || !this.selected.length ||
      this.selected.length > 30 || this.chars > cfg.massimo_caratteri
  }

  async run() {
    if (this.avvioSpento()) return
    const gen = this.gen
    this.busy = true
    this.error = ""
    this.render()
    try {
      const r = await apiJson(this.base, {
        method: "POST",
        body: {
          documenti: [...this.selected], focus: this.focus, documenti_pubblici_confermati: this.confirmed,
          ...(this.provider === "mistral" ? { provider: this.provider } : {}),
          ...(this.resume ? { riprendi_quesiti: true } : {}),
        },
      })
      if (!this.attuale(gen)) return
      this.archived = null
      this.waiting = r.run_id
      await this.load()
    } catch (e) {
      if (this.attuale(gen)) this.mostraErrore(e)
    } finally {
      if (this.attuale(gen)) {
        this.busy = false
        this.render()
      }
    }
  }

  async openHistory(runId) {
    const gen = this.gen
    this.historyBusy = true
    this.error = ""
    this.render()
    try {
      const d = await apiJson(`${this.base}/sessioni/${encodeURIComponent(runId)}${this.query}`)
      if (this.attuale(gen)) this.archived = d
    } catch (e) {
      if (this.attuale(gen)) this.error = err(e)
    } finally {
      if (this.attuale(gen)) {
        this.historyBusy = false
        this.render()
      }
    }
  }

  // ---- disegno ---------------------------------------------------------------------------------------------------

  bottone(testoBottone, { variante = "pulsante", ...attrs } = {}) {
    return h("button", { type: "button", class: this.classiValue[variante], ...attrs }, testoBottone)
  }

  render() {
    if (!this.attivo) return
    const D = this.disegno
    const nodi = [this.titoloTarget, this.introTarget]
    if (this.error) nodi.push(D.nodo("errore", this.error, () => h("p", { role: "alert", class: "text-sm text-rose-700" }, this.error)))
    if (!this.dati) {
      nodi.push(D.nodo("carica", "", () => this.bottone("Carica l’istruttoria", { variante: "contorno", on: { click: () => this.ricarica() } })))
    } else {
      nodi.push(...this.corpo())
    }
    nodi.push(this.metodoTarget)
    sincronizza(this.sezioneTarget, nodi)
    D.fine()
    this.programma()
  }

  corpo() {
    const D = this.disegno
    const { dati: data, nome } = this
    const cfg = data.configurazione
    const session = data.sessione
    const running = this.running
    const nodi = []
    const p = (chiave, classe, t, attrs = {}) => D.nodo(chiave, t, () => h("p", { class: classe, ...attrs }, t))

    nodi.push(p("cfg", "text-xs text-zinc-500",
                `${nome} · ${testo(cfg.modello)} · metodo ${testo(cfg.metodo)} · massimo ${testo(cfg.massimo_chiamate)} chiamate per istruttoria, con consumi API.`))
    if (!cfg.configurata) {
      nodi.push(p("non-configurato", "text-sm text-amber-800",
                  `${nome} non configurato. Per attivarlo serve ${this.provider === "mistral" ? "MISTRAL_API_KEY" : "OPENAI_API_KEY"} sul backend erogo-api; non inserire chiavi in questa pagina o in chat. Le funzioni Mistral esistenti restano disponibili.`,
                  { "data-testid": "istruttoria-config" }))
    }
    nodi.push(this.documenti())
    if (!data.documenti.length) nodi.push(p("nessun-documento", "text-sm text-zinc-500", "Importa prima i documenti ufficiali del bando."))
    nodi.push(this.perimetro())

    const ultimo = session?.ultimo
    const canResume = ultimo?.attualita === "attuale" &&
      [...(ultimo.rapporto?.quesiti || []), ...(ultimo.revisione_critica?.punti_da_chiarire || [])].some((q) => q.trim())
    const ripresa = D.nodo("ripresa", nome, () => {
      const input = h("input", { type: "checkbox", on: { change: (e) => { this.resume = e.target.checked; this.confirmed = false; this.render() } } })
      const l = h("label", { class: "flex gap-2 text-sm" }, input,
                  `Riprendi i quesiti aperti dell’ultimo rapporto ${nome}. Servono gli stessi documenti, perimetro e dati attuali; le risposte precedenti non sono considerate approvate.`)
      l.casella = input
      return l
    })
    ripresa.casella.checked = this.resume
    ripresa.casella.disabled = !canResume
    nodi.push(ripresa)

    nodi.push(p("caratteri", "text-xs text-zinc-500",
                `${num(this.chars)} su ${num(cfg.massimo_caratteri)} caratteri; massimo 30 documenti. I testi selezionati non vengono tagliati per rientrare nel limite.`))

    const consenso = D.nodo("consenso", nome, () => {
      const input = h("input", { type: "checkbox", on: { change: (e) => { this.confirmed = e.target.checked; this.render() } } })
      const l = h("label", { class: "flex gap-2 text-sm" }, input,
                  `Confermo che i documenti selezionati e il perimetro contengono soltanto informazioni pubbliche del bando e autorizzo l’invio a ${nome} per questa istruttoria.`)
      l.casella = input
      return l
    })
    consenso.casella.checked = this.confirmed
    nodi.push(consenso)

    const azioni = D.nodo("azioni", "", () => {
      const avvio = this.bottone("", { on: { click: () => this.run() } })
      const aggiorna = this.bottone("Aggiorna istruttoria", { variante: "contorno", on: { click: () => this.ricarica() } })
      const div = h("div", { class: "flex gap-2 flex-wrap" }, avvio, aggiorna)
      Object.assign(div, { avvio, aggiorna })
      return div
    })
    azioni.avvio.textContent = running || this.waiting ? "Istruttoria in corso…" : `Avvia istruttoria ${nome}`
    azioni.avvio.disabled = this.avvioSpento()
    azioni.aggiorna.disabled = this.busy
    nodi.push(azioni)

    if (running && session?.fase) nodi.push(p("fase", "text-sm text-zinc-600", testo(session.fase), { role: "status" }))
    if (session?.errore) nodi.push(p("errore-sessione", "text-sm text-rose-700", testo(session.errore), { role: "alert" }))
    if (session?.seconda_lettura_riprendibile) {
      nodi.push(p("riprendibile", "text-sm text-amber-800",
                  "Prima lettura conservata, non approvata. Un nuovo avvio con gli stessi documenti, perimetro e dati riprende soltanto la seconda lettura critica, con nuovi consumi API."))
    }
    if (session?.stato === "errore" && session.uso_parziale?.length) {
      nodi.push(p("uso-parziale", "text-xs text-zinc-500",
                  `Consumi noti della sessione interrotta: ${token(session.uso_parziale, "input_tokens")} token in ingresso · ${token(session.uso_parziale, "output_tokens")} token in uscita. Incluse eventuali fasi riprese; il consumo della richiesta senza risposta può non essere disponibile. Nessun rapporto approvato.`))
    }
    if (data.precedenti?.length) nodi.push(this.storico(data.precedenti))
    if (this.archived) nodi.push(this.archiviata(this.archived))
    const result = this.archived ? this.archived.rapporto : session?.ultimo
    if (result) {
      nodi.push(D.nodo("rapporto", JSON.stringify([result, session?.run_id]), () => this.rapporto(result, session)))
    }
    return nodi
  }

  documenti() {
    const docs = this.dati.documenti
    const el = this.disegno.nodo("documenti", JSON.stringify(docs), () => {
      const summary = h("summary", { class: "font-semibold text-sm cursor-pointer" })
      const caselle = new Map()
      const righe = docs.map((d) => {
        const input = h("input", { type: "checkbox", on: { change: (e) => {
          this.change(e.target.checked ? [...this.selected, d.doc_id] : this.selected.filter((x) => x !== d.doc_id))
        } } })
        caselle.set(d.doc_id, input)
        return h("label", { class: "flex gap-2 text-sm border rounded p-2" }, input,
                 h("span", {}, testo(d.name), h("span", { class: "block text-xs text-zinc-500" },
                                                `${num(d.caratteri)} caratteri · ${d.completo ? "testo integrale disponibile" : d.motivo || "lettura integrale non attestata"}`)))
      })
      const details = h("details", { "data-chiave": "documenti" }, summary,
                        h("div", { class: "flex gap-2 mt-2" },
                          this.bottone("Seleziona tutti", { variante: "contornoPiccolo", on: { click: () => this.change(this.dati.documenti.map((d) => d.doc_id)) } }),
                          this.bottone("Svuota selezione", { variante: "contornoPiccolo", on: { click: () => this.change([]) } })),
                        h("div", { class: "max-h-72 overflow-auto space-y-2 mt-2" }, righe))
      Object.assign(details, { summary, caselle })
      return details
    })
    el.summary.textContent = `Documenti da sottoporre all’istruttoria (${this.selected.length} selezionati)`
    el.caselle.forEach((input, id) => { input.checked = this.selected.includes(id) })
    return el
  }

  perimetro() {
    const el = this.disegno.nodo("perimetro", "", () => {
      const area = h("textarea", {
        class: "mt-1 block w-full rounded border p-2 text-sm", maxlength: 1000,
        placeholder: "Per esempio: rendicontazione con conto vincolato e applicabilità dei moduli. Non inserire dati di imprese o persone.",
        on: { input: (e) => { this.focus = e.target.value; this.confirmed = false; this.resume = false; this.render() } },
      })
      const l = h("label", { class: "block text-sm" }, "Perimetro della verifica (facoltativo)", area)
      l.area = area
      return l
    })
    if (el.area.value !== this.focus) el.area.value = this.focus
    return el
  }

  storico(precedenti) {
    const el = this.disegno.nodo("storico", JSON.stringify([precedenti, this.nome]), () => {
      const bottoni = []
      const d = h("details", { class: "border-t pt-3 text-sm", "data-testid": "istruttoria-storico" },
                  h("summary", { class: "cursor-pointer font-semibold" }, `Storico delle ultime cinque sessioni ${this.nome}`),
                  precedenti.map((p) => {
                    const b = this.bottone("Apri sessione", { variante: "contornoPiccolo", on: { click: () => this.openHistory(p.run_id) } })
                    bottoni.push(b)
                    return h("div", { class: "flex flex-wrap items-center gap-2 mt-2" },
                             h("span", {}, `${quando(p.fine)} · ${p.stato === "conclusa" ? "rapporto da riesaminare" : "sessione non completata"} · ${p.perimetro || "Documenti selezionati"}`),
                             b)
                  }))
      d.bottoni = bottoni
      return d
    })
    el.bottoni.forEach((b) => { b.disabled = this.historyBusy })
    return el
  }

  archiviata(a) {
    return this.disegno.nodo("archiviata", JSON.stringify(a), () => h("div", { class: "text-sm border rounded p-3", "data-testid": "istruttoria-archiviata" },
      h("p", {}, `Sessione archiviata del ${quando(a.fine)}${a.errore ? ` — ${a.errore}` : ""}`),
      a.uso_parziale?.length ? h("p", {}, `Consumi noti archiviati: ${token(a.uso_parziale, "input_tokens")} token in ingresso · ${token(a.uso_parziale, "output_tokens")} token in uscita. Il consumo della richiesta senza risposta può non essere disponibile.`) : null,
      this.bottone("Mostra ultimo rapporto", { variante: "contornoPiccolo", on: { click: () => { this.archived = null; this.render() } } })))
  }

  rapporto(result, session) {
    const li = (x) => h("li", {}, testo(x))
    const ul = (lista) => h("ul", { class: "list-disc pl-5" }, lista.map(li))
    const esito = (r) => (r.esito === "da_chiarire" ? "Da chiarire" : r.esito === "contrasto" ? "Contrasto da riesaminare" : "Proposta")
    return h("div", { class: "space-y-3 border-t pt-3", "data-testid": "istruttoria-rapporto" },
      h("p", { class: "font-semibold text-amber-800" },
        `Rapporto da riesaminare${result.attualita === "superata" ? " — superato da modifiche a fonti, dati o metodo" : ""}${result.run_id !== session?.run_id ? " — rapporto precedente alla sessione attuale" : ""}`),
      h("p", { class: "text-xs text-zinc-500" }, `${quando(result.conclusa_il)} · ${(result.modelli || []).join(", ")} · ${testo(result.chiamate)} chiamate`),
      result.uso?.length ? h("p", { class: "text-xs text-zinc-500" }, `Consumi della sessione conclusa: ${token(result.uso, "input_tokens")} token in ingresso · ${token(result.uso, "output_tokens")} token in uscita. Non include sessioni precedenti o fallite.`) : null,
      result.seconda_lettura_ripresa ? h("p", { class: "text-xs text-zinc-600" }, `Seconda lettura ripresa dalla prima lettura conservata. Consumi noti delle fasi precedenti: ${token(result.uso_precedente, "input_tokens")} token in ingresso · ${token(result.uso_precedente, "output_tokens")} token in uscita, separati da questa sessione.`) : null,
      h("p", { class: "text-sm" }, testo(result.rapporto.sintesi)),
      result.memoria_usata ? h("p", { class: "text-xs text-zinc-600" }, `Ripresi ${result.memoria_usata.quesiti_non_validati.length} quesiti della sessione ${testo(result.memoria_usata.run_id)}, da riesaminare sulle fonti.`) : null,
      this.percorso(result.percorso),
      h("details", { class: "text-sm", "data-chiave": "controlli" },
        h("summary", { class: "cursor-pointer font-medium" }, "Controlli software sullo stato attuale"),
        h("p", {}, result.controlli_software?.stato || "Nessun controllo disponibile"),
        h("ul", { class: "list-disc pl-5" }, (result.controlli_software?.ragioni || []).map((x) => h("li", {}, testo(x.testo)))),
        h("p", { class: "text-xs text-zinc-500" }, "È lo stato calcolato sui dati attuali; le nuove prove proposte dall’AI restano da eseguire.")),
      h("details", { class: "text-sm", "data-chiave": "fonti" },
        h("summary", { class: "cursor-pointer font-medium" }, "Consultazioni aggiuntive delle fonti"),
        !result.fonti_consultate?.length
          ? h("p", {}, "Nessuna fonte aggiuntiva consultata in questa sessione.")
          : result.fonti_consultate.map((x) => h("p", { class: "break-all" }, `${testo(x.url)} — ${testo(x.esito)}${x.completo === false ? "; lettura integrale non attestata" : ""}`))),
      result.riscontri?.length ? h("ul", { class: "text-sm text-amber-800 list-disc pl-5" }, result.riscontri.map(li)) : null,
      result.rapporto.rilievi.map((r, i) => h("article", { class: "border rounded p-3 space-y-2 text-sm" },
        h("h3", { class: "font-semibold" }, `${testo(r.tema)} — ${esito(r)}`),
        h("p", {}, testo(r.conclusione)), h("p", { class: "text-zinc-600" }, `Ambito: ${testo(r.ambito)}`),
        r.condizioni.length > 0 ? ul(r.condizioni) : null,
        r.evidenze.map((e) => h("blockquote", { class: "border-l-2 pl-3 text-zinc-600" },
          h("p", {}, `${testo(e.documento)} · ${testo(e.riscontro.punto || e.punto)} — ${e.frase_riscontrata ? "frase riscontrata nel testo" : "citazione da verificare"}${!e.fonte_completa ? "; fonte incompleta" : ""}`),
          h("p", {}, `«${testo(e.frase)}»`),
          e.riscontro.nota ? h("p", {}, testo(e.riscontro.nota)) : null)),
        h("details", { "data-chiave": `prove-${i}` },
          h("summary", { class: "cursor-pointer font-medium" }, "Prove proposte, non eseguite"),
          h("p", {}, `Positiva: ${testo(r.controllo_positivo)}`), h("p", {}, `Negativa: ${testo(r.controllo_negativo)}`),
          h("p", {}, `Dati ignoti: ${testo(r.controllo_dati_ignoti)}`), h("p", {}, `Riscontro manuale: ${testo(r.verifica_manuale)}`)))),
      h("div", { class: "text-sm" }, h("b", {}, "Quesiti e limiti"), ul([...result.rapporto.quesiti, ...result.rapporto.limiti])),
      h("div", { class: "text-sm" }, h("b", {}, "Seconda lettura critica AI"), h("p", {}, testo(result.revisione_critica.motivazione)),
        ul([...result.revisione_critica.osservazioni, ...result.revisione_critica.punti_da_chiarire]),
        h("p", { class: "text-xs text-zinc-500" }, "È una seconda lettura dello stesso modello, non una certificazione indipendente.")),
      h("p", { class: "text-xs text-zinc-500" }, testo(result.avviso)))
  }

  percorso(value) {
    if (!value) return h("p", { class: "text-xs text-zinc-500" }, "Questo rapporto precede il registro del lavoro residuo. Nessun esito di collaudo viene ricostruito retroattivamente.")
    return h("details", { class: "text-sm border rounded p-3", "data-testid": "istruttoria-percorso", "data-chiave": "percorso" },
      h("summary", { class: "cursor-pointer font-semibold" }, "Metodo applicato e lavoro residuo — aperto"),
      h("p", { class: "my-2" }, `${testo(value.fonti_integrali)} su ${testo(value.fonti_selezionate)} documenti selezionati con testo integrale attestato. Il dato riguarda la selezione, non la completezza del fascicolo.`),
      h("p", {}, `${value.riscontri_citazioni_eseguiti.length} riscontri di citazioni eseguiti dal software. Prove di funzionamento delle nuove regole eseguite: ${testo(value.prove_funzionamento_eseguite)}.`),
      value.rilievi.map((r) => h("div", { class: "border-t mt-2 pt-2" },
        h("b", {}, `${testo(r.rilievo)}. ${testo(r.tema)}`), ` — ${r.stato === "da_chiarire" ? "da chiarire" : "da verificare"}`,
        r.motivi.length ? h("ul", { class: "list-disc pl-5" }, r.motivi.map((x) => h("li", {}, testo(x)))) : null,
        h("p", {}, `Prove: ${r.prove.map((p) => `${p.tipo === "dati_ignoti" ? "dati ignoti" : testo(p.tipo)}: ${p.stato === "mancante" ? "da definire" : "da eseguire"}`).join(" · ")}`),
        h("p", {}, `Verifica manuale: ${r.verifica_manuale || "da definire"}`))),
      h("p", { class: "font-semibold mt-3" }, "Prossime azioni"),
      h("ul", { class: "list-disc pl-5" }, value.prossime_azioni.map((x) => h("li", {}, testo(x)))),
      h("p", { class: "text-xs text-zinc-500 mt-2" }, testo(value.limite)))
  }

  // ---- prova del metodo sui casi sintetici (ProvaMetodo) ---------------------------------------------------------

  async loadMetodo() {
    const gen = this.gen
    const d = await apiJson("/api/admin/istruttoria-ai/metodo")
    if (!this.attuale(gen)) throw Object.assign(new Error("superata"), { superata: true })
    this.mData = d
    this.renderMetodo()
    return d
  }

  programmaMetodo() {
    const running = this.mData?.prove?.some((p) => p.stato === "in_corso")
    const serve = running || !!this.mWaiting
    if (serve && !this.timerMetodo) {
      this.timerMetodo = setInterval(() => {
        this.loadMetodo().then((d) => {
          if (this.mWaiting && d.prove.some((p) => p.run_id === this.mWaiting && p.stato !== "in_corso")) {
            this.mWaiting = null
            this.renderMetodo()
          }
        }).catch((e) => {
          if (e?.superata || !this.attivo) return
          this.mError = err(e)
          this.mWaiting = null
          this.renderMetodo()
        })
      }, this.intervalloValue)
    } else if (!serve && this.timerMetodo) {
      clearInterval(this.timerMetodo)
      this.timerMetodo = null
    }
    this.segnaRilettura()
  }

  async prova(provider) {
    if (this.mBusy || this.mWaiting) return
    const gen = this.gen
    this.mBusy = true
    this.mError = ""
    this.renderMetodo()
    try {
      const r = await apiJson("/api/admin/istruttoria-ai/metodo", { method: "POST", body: { provider, conferma: true } })
      if (!this.attuale(gen)) return
      this.mWaiting = r.run_id
      await this.loadMetodo()
    } catch (e) {
      if (this.attuale(gen) && !e?.superata) this.mError = err(e)
    } finally {
      if (this.attuale(gen)) {
        this.mBusy = false
        this.renderMetodo()
      }
    }
  }

  renderMetodo() {
    if (!this.attivo) return
    const D = this.disegnoMetodo
    const nodi = [this.metodoTitoloTarget, this.metodoIntroTarget]
    if (this.mError) nodi.push(D.nodo("errore", this.mError, () => h("p", { role: "alert", class: "text-sm text-rose-700" }, this.mError)))
    const azioni = D.nodo("azioni", "", () => {
      const mistral = this.bottone("Prova metodo con Mistral", { variante: "contornoPiccolo", on: { click: () => this.prova("mistral") } })
      const openai = this.bottone("Prova metodo con OpenAI", { variante: "contornoPiccolo", on: { click: () => this.prova("openai") } })
      const div = h("div", { class: "flex gap-2 flex-wrap" }, mistral, openai)
      Object.assign(div, { mistral, openai })
      return div
    })
    azioni.mistral.disabled = this.mBusy || !!this.mWaiting || !this.mData?.mistral_disponibile
    azioni.openai.disabled = this.mBusy || !!this.mWaiting || !this.mData?.openai_disponibile
    nodi.push(azioni)
    if (this.mWaiting) nodi.push(D.nodo("attesa", "", () => h("p", { class: "text-sm text-zinc-600 mt-2" }, "Prova in corso…")))
    ;(this.mData?.prove || []).forEach((p) => {
      nodi.push(D.nodo(`prova-${p.provider}`, JSON.stringify(p), () => h("div", { class: "text-sm mt-3 border rounded p-3" },
        h("b", {}, p.provider === "openai" ? "OpenAI" : "Mistral"),
        ` — ${p.stato === "conclusa" ? "prova conclusa" : p.stato === "in_corso" ? "in corso" : "prova non completata"}`,
        p.errore ? h("p", { class: "text-rose-700" }, testo(p.errore)) : null,
        p.risultato && p.risultato.run_id === p.run_id ? [
          h("p", {}, `${testo(p.risultato.superati)} su ${testo(p.risultato.totale)} casi con esito atteso · ${testo(p.risultato.modello)}`),
          h("ul", { class: "list-disc pl-5" }, p.risultato.casi.map((c) => h("li", {},
            `${testo(c.id)}: ${c.superato ? "coerente" : "da riesaminare"} — atteso ${testo(c.atteso)}, osservato ${testo(c.osservato)}. ${testo(c.motivazione)}${!c.frase_riscontrata ? " Frase non riscontrata." : ""}`))),
          h("p", { class: "text-xs text-zinc-500 mt-2" }, testo(p.risultato.limite)),
        ] : null)))
    })
    sincronizza(this.metodoTarget, nodi)
    D.fine()
    this.programmaMetodo()
  }
}
